/* @jsx h */
import { defineComponent, Fragment, h, ref } from "vue";
import { ThemeSwitcher, useLocalization } from "@lemmonhq/uikit";
import { getSupportedLanguagesForJurisdictions } from "@app/utils/supportedLanguages";
import { useGroupAuth } from "@app/vue/providers/GroupAuth";
import { useToast } from "@app/vue/providers/Toast";
import { AccountMenu } from "./AccountMenu";
import { ReleaseInfo } from "./ReleaseInfo";
import { RestaurantSwitcherImage } from "./RestaurantSwitcherImage";

export const GroupQuickLinks = defineComponent({
  name: "GroupQuickLinks",
  setup() {
    const auth = useGroupAuth();
    const toast = useToast();
    const { t, locale, setLocale } = useLocalization();
    const loadingLanguage = ref<string | null>(null);
    const openingUserId = ref<number | null>(null);

    const openShop = async (userId: number) => {
      if (openingUserId.value !== null) return;
      openingUserId.value = userId;
      try {
        await auth.exitToShop(userId);
      } catch {
        openingUserId.value = null;
        toast.showToast(t("admin.shops.open_error"), { type: "error" });
      }
    };

    const setLanguage = async (language: string) => {
      loadingLanguage.value = language;
      await setLocale(language);
      loadingLanguage.value = null;
    };

    return () => {
      const availableShops = auth.shops.value.filter(
        (shop) =>
          shop.can_open_pos && shop.user_id && shop.pos_role === "manager",
      );
      const availableLanguages = getSupportedLanguagesForJurisdictions(
        auth.shops.value.map((shop) => shop.jurisdiction),
      );

      return (
        <AccountMenu
          name={auth.identity.value.name}
          contextName={auth.group.value.name}
          showHeaderDivider
          renderItems={() => (
            <Fragment>
              {availableShops.length > 0 && (
                <Fragment>
                  <li class="pointer-events-none px-3 pt-1.5 pb-0.5">
                    <span class="p-0 text-[10px] font-semibold uppercase tracking-widest text-base-content/30 hover:bg-transparent">
                      {t("admin.nav.return_to_pos")}
                    </span>
                  </li>
                  {availableShops.map((shop) => (
                    <li class="py-0.5" key={shop.id}>
                      <button
                        type="button"
                        disabled={openingUserId.value !== null}
                        onClick={() => void openShop(shop.user_id!)}
                        class="flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-[13px] text-base-content/60 transition-colors duration-150 hover:text-accent"
                      >
                        {openingUserId.value === shop.user_id ? (
                          <span class="loading loading-spinner loading-xs" />
                        ) : (
                          <RestaurantSwitcherImage
                            name={shop.name}
                            url={shop.image_url}
                          />
                        )}
                        <span class="min-w-0 truncate">{shop.name}</span>
                      </button>
                    </li>
                  ))}
                  <div class="mx-2 my-1 h-px bg-base-content/10" />
                </Fragment>
              )}

              <li class="pointer-events-none px-3 pt-1.5 pb-0.5">
                <span class="p-0 text-[10px] font-semibold uppercase tracking-widest text-base-content/30 hover:bg-transparent">
                  {t("admin.nav.language")}
                </span>
              </li>
              {availableLanguages.map((language) => (
                <li class="py-0.5" key={language.id}>
                  <button
                    type="button"
                    onClick={() => void setLanguage(language.id)}
                    class={`flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-[13px] transition-colors duration-150 ${
                      locale.value === language.id
                        ? "bg-accent/10 text-accent"
                        : "text-base-content/60 hover:text-accent"
                    }`}
                  >
                    {loadingLanguage.value === language.id ? (
                      <span class="loading loading-spinner loading-xs" />
                    ) : (
                      <span class="material-symbols-outlined text-[18px]">
                        {locale.value === language.id
                          ? "radio_button_checked"
                          : "radio_button_unchecked"}
                      </span>
                    )}
                    {language.name}
                  </button>
                </li>
              ))}

              <div class="mx-2 my-1 h-px bg-base-content/10" />

              {import.meta.env.VITE_ENV === "staging" && (
                <Fragment>
                  <li class="pointer-events-none px-3 pt-1.5 pb-0.5">
                    <span class="p-0 text-[10px] font-semibold uppercase tracking-widest text-base-content/30 hover:bg-transparent">
                      {t("admin.nav.theme")}
                    </span>
                  </li>
                  <ThemeSwitcher />
                </Fragment>
              )}
            </Fragment>
          )}
          renderFooter={() => (
            <Fragment>
              <div class="mx-2 my-1 h-px bg-base-content/10" />
              <li>
                <button
                  type="button"
                  class="flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] text-error/70 transition-colors duration-150 hover:bg-error/5 hover:text-error"
                  onClick={() => void auth.logout()}
                >
                  <span class="material-symbols-outlined text-[18px]">
                    logout
                  </span>
                  {t("admin.common.logout")}
                </button>
              </li>

              <ReleaseInfo />
            </Fragment>
          )}
        />
      );
    };
  },
});
